import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@wa/ui";

export type BtnTone = "plain" | "dark" | "brand" | "danger";

const TONE: Record<BtnTone, string> = {
  plain: "",
  dark: "crm-btn-dark",
  brand: "crm-btn-brand",
  danger: "crm-btn-danger",
};

/**
 * The pill button. `tone` picks the surface: plain (a raised white pill), dark
 * (the inverted primary action), brand (the gradient) or danger.
 *
 * `type` defaults to "button" for the same reason the shared Button does it: a
 * bare <button> inside a <form> submits, and half of these live inside forms.
 */
export const Btn = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { tone?: BtnTone; small?: boolean; round?: boolean; children?: ReactNode }
>(function Btn({ tone = "plain", small, round, className, children, type, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn("crm-btn", TONE[tone], small && "crm-btn-sm", round && "crm-btn-r", className)}
      {...rest}
    >
      {children}
    </button>
  );
});

/**
 * The quiet circular control that lives in a card head or next to a field: a
 * hairline ring that fills in on hover. Deliberately not a Btn - it must not
 * compete with the page's real actions.
 */
export const IconBtn = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { small?: boolean; wide?: boolean; children?: ReactNode }
>(function IconBtn({ small, wide, className, children, type, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn("crm-cb", small && "crm-cb-sm", wide && "crm-cb-wide", className)}
      {...rest}
    >
      {children}
    </button>
  );
});
